import { ArrowDown, ArrowUp } from "lucide-react";
import type { ReactNode } from "react";
import type { FieldDefinition, Sort } from "@estelle/domain";

import { sortPositionOf } from "@/components/table/sorting";
import { cn } from "@/lib/utils";

/**
 * A sortable column header, controlled.
 *
 * Renders the whole `<th>` rather than just the button inside it, because
 * `aria-sort` belongs on the header cell, not the button — a screen reader
 * announces sort state from the cell, and an arrow glyph alone tells a
 * sighted user "descending" but tells a screen reader nothing at all.
 *
 * Shift-click (or Shift+Enter, since a keyboard-triggered click event still
 * carries the modifier state) requests the additive, multi-column toggle;
 * a plain activation always collapses to a single-column sort. See
 * `toggleSort` for why that split exists.
 */
export function SortableHeader({
  field,
  sort,
  onToggle,
  className,
  extra,
}: {
  field: FieldDefinition;
  sort: Sort[];
  onToggle: (field: FieldDefinition, additive: boolean) => void;
  className?: string;
  /** Beside the label: Stage's legend button (#643). */
  extra?: ReactNode;
}) {
  if (!field.sortable) {
    /*
      The size, weight and colour of a sortable header that is not the sorted
      one (#460). A bare `<th>` is the browser's bold, in the foreground colour,
      so on a table that mixes the two the plain headers read darker than the
      clickable ones, and a dark header looks like "this is the sorted column".
      Only the active sort is foreground.
    */
    return (
      <th
        scope="col"
        className={cn("text-muted-foreground text-sm font-medium", className)}
      >
        {field.label}
        {extra}
      </th>
    );
  }

  const position = sortPositionOf(sort, field.name);
  const ariaSort =
    position === null
      ? "none"
      : position.direction === "asc"
        ? "ascending"
        : "descending";

  return (
    <th scope="col" aria-sort={ariaSort} className={className}>
      <button
        type="button"
        onClick={(event) => onToggle(field, event.shiftKey)}
        className={cn(
          "inline-flex items-center gap-1 text-sm font-normal",
          "hover:text-foreground text-muted-foreground",
          position !== null && "text-foreground font-medium",
          "focus-visible:ring-ring/50 rounded-sm outline-none focus-visible:ring-[3px]",
        )}
      >
        <span>{field.label}</span>
        {position !== null ? (
          <span className="inline-flex items-center gap-0.5 tabular-nums">
            {/* The ordinal only earns its place once there is more than one
                sorted column — otherwise "1 ↓" is noise nobody asked for. */}
            {sort.length > 1 ? (
              <span className="text-xs">{position.index}</span>
            ) : null}
            {position.direction === "asc" ? (
              <ArrowUp className="size-3.5" />
            ) : (
              <ArrowDown className="size-3.5" />
            )}
          </span>
        ) : null}
      </button>
      {extra}
    </th>
  );
}
